// API client service
// Reference: apps/api routes, contracts/openapi.yaml

import type { ApiError } from '@propman/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | undefined;
  body?: unknown | undefined;
  headers?: Record<string, string> | undefined;
  params?: Record<string, string | number | boolean | undefined> | undefined;
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  private buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}/api/v1${path}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, params } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (this.authToken) {
      requestHeaders['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(this.buildUrl(path, params), {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : null,
      credentials: 'include',
    });

    const data = await response.json() as T | ApiError;

    if (!response.ok) {
      const error = data as ApiError;
      throw new ApiRequestError(
        error.error?.message || 'Request failed',
        response.status,
        error.error?.code || 'UNKNOWN_ERROR',
        error.error?.details
      );
    }

    return data as T;
  }

  // Generic methods
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>(path, { method: 'GET', params });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  // File upload method
  async uploadFile(path: string, file: File): Promise<unknown> {
    const requestHeaders: Record<string, string> = {};

    if (this.authToken) {
      requestHeaders['Authorization'] = `Bearer ${this.authToken}`;
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(this.buildUrl(path), {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new ApiRequestError(
        error.error?.message || 'Upload failed',
        response.status,
        error.error?.code || 'UPLOAD_ERROR',
        error.error?.details
      );
    }

    return data;
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidationError(): boolean {
    return this.status === 400 && this.code === 'VALIDATION_ERROR';
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);
export const apiClient = api;

// Helper to build attachment URL with auth token
export function getAttachmentUrl(issueId: string, attachmentId: string): string {
  const token = api.getAuthToken();
  const baseUrl = `${API_BASE_URL}/api/v1/issues/${issueId}/attachments/${attachmentId}`;
  return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
}

// Re-export for direct imports
export default api;
